import React from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";

const accessLabels = {
  cross: "CROSS",
  funcional: "FUNCIONAL",
  maquinas: "MAQUINAS",
};

export default function MembershipsTable({
  plans,
  canManage,
  actionLoadingId,
  onEdit,
  onDelete,
}) {
  const getDurationLabel = (item) => {
    if (!item?.duration_value) return "Sin duración";
    if (item.duration_value === 1) return "1 día";
    return `${item.duration_value} días`;
  };

  const getUsageLabel = (item) => {
    if (item?.usage_type === "visits") return "Por visitas";
    return "Por tiempo";
  };

  const getAccessesLabel = (item) => {
    if (!Array.isArray(item?.available_accesses) || !item.available_accesses.length) {
      return "Sin accesos";
    }

    return item.available_accesses
      .map((access) => accessLabels[access] || String(access).toUpperCase())
      .join(" · ");
  };

  return (
    <Paper
      sx={{
        borderRadius: "18px",
        border: "1px solid rgba(148,163,184,0.18)",
        boxShadow: "0 18px 40px rgba(15,23,42,0.06)",
        overflow: "hidden",
        bgcolor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1.5fr 0.9fr",
            md: "1.6fr 0.9fr 0.9fr 0.8fr 1fr",
          },
          gap: 1,
          px: { xs: 1.6, md: 2 },
          py: 1.3,
          bgcolor: "#f8fafc",
          borderBottom: "1px solid rgba(148,163,184,0.16)",
        }}
      >
        <Typography sx={{ fontSize: 12.6, fontWeight: 600, color: "#475569" }}>
          Membresía
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Precio
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Duración
        </Typography>
        <Typography sx={{ fontSize: 12.6, fontWeight: 600, color: "#475569" }}>
          Estado
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", md: "block" },
            fontSize: 12.6,
            fontWeight: 600,
            color: "#475569",
          }}
        >
          Acción
        </Typography>
      </Box>

      {!plans.length ? (
        <Box sx={{ px: 2, py: 4 }}>
          <Typography sx={{ fontSize: 13.4, fontWeight: 500, color: "#64748b" }}>
            No se encontraron membresías.
          </Typography>
        </Box>
      ) : (
        plans.map((item) => (
          <Box
            key={item.id}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1.5fr 0.9fr",
                md: "1.6fr 0.9fr 0.9fr 0.8fr 1fr",
              },
              gap: 1,
              alignItems: "center",
              px: { xs: 1.6, md: 2 },
              py: 1.25,
              borderBottom: "1px solid rgba(148,163,184,0.12)",
              bgcolor: "#ffffff",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: "#0f172a",
                  fontSize: 13.4,
                  fontWeight: 600,
                  lineHeight: 1.35,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {item.name || "Sin nombre"}
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 12.2,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  mt: 0.3,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getUsageLabel(item)}
              </Typography>

              <Typography
                sx={{
                  color: "#64748b",
                  fontSize: 12.1,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  mt: 0.25,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {getAccessesLabel(item)}
              </Typography>

              <Typography
                sx={{
                  display: { xs: "block", md: "none" },
                  color: "#334155",
                  fontSize: 12.1,
                  fontWeight: 500,
                  lineHeight: 1.35,
                  mt: 0.45,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                ${item.price || "0.00"} · {getDurationLabel(item)}
              </Typography>
            </Box>

            <Typography
              sx={{
                display: { xs: "none", md: "block" },
                color: "#334155",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              ${item.price || "0.00"}
            </Typography>

            <Typography
              sx={{
                display: { xs: "none", md: "block" },
                color: "#334155",
                fontSize: 13,
                fontWeight: 500,
                lineHeight: 1.35,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {getDurationLabel(item)}
            </Typography>

            <Box>
              <Chip
                label={item.is_active ? "Activa" : "Inactiva"}
                size="small"
                sx={{
                  height: 26,
                  borderRadius: "8px",
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: item.is_active
                    ? "rgba(59,130,246,0.10)"
                    : "rgba(100,116,139,0.12)",
                  color: item.is_active ? "#1d4ed8" : "#475569",
                }}
              />
            </Box>

            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {canManage && (
                <Button
                  onClick={() => onEdit(item)}
                  variant="outlined"
                  disabled={actionLoadingId === String(item.id)}
                  sx={{
                    height: 34,
                    px: 1.2,
                    borderRadius: "9px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12.6,
                    color: "#334155",
                    bgcolor: "#ffffff",
                    borderColor: "rgba(51,65,85,0.18)",
                    "&:hover": {
                      borderColor: "rgba(51,65,85,0.28)",
                      bgcolor: "#f8fafc",
                    },
                  }}
                >
                  Editar
                </Button>
              )}

              {canManage && (
                <Button
                  onClick={() => onDelete(item)}
                  variant="outlined"
                  disabled={actionLoadingId === String(item.id)}
                  startIcon={<DeleteRoundedIcon />}
                  sx={{
                    height: 34,
                    px: 1.2,
                    borderRadius: "9px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12.6,
                    color: "#dc2626",
                    bgcolor: "#ffffff",
                    borderColor: "rgba(220,38,38,0.22)",
                    "&:hover": {
                      borderColor: "rgba(220,38,38,0.32)",
                      bgcolor: "rgba(220,38,38,0.06)",
                    },
                  }}
                >
                  Eliminar
                </Button>
              )}
            </Stack>
          </Box>
        ))
      )}
    </Paper>
  );
}